import { ApolloError } from '@apollo/client';
import { User } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useUsersPageCreateUserMutation } from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type CreateUserInput = {
  name: string;
  email?: string;
  phone?: string;
  locale?: string;
  isTest: boolean;
  supplier: boolean;
  loginMethod: 'phone' | 'email' | 'emailAndPhone';
};

export type UseCreateUserResponse = {
  createUser: (input: CreateUserInput) => Promise<User>;
  isLoading: boolean;
};

export type UseCreateUserConfig = {
  captureException?: CaptureExceptionFunction;
};

const mapLoginMethod = (method: string) => {
  switch (method) {
    case 'phone':
      return 'phone' as const;
    case 'email':
      return 'email' as const;
    case 'emailAndPhone':
      return 'emailAndPhone' as const;
    default:
      return 'phone' as const;
  }
};

export const useCreateUser = (
  config: UseCreateUserConfig
): UseCreateUserResponse => {
  const { addNotification } = useNotification();
  const [createUserMutation, { loading }] = useUsersPageCreateUserMutation();

  const createUser = useCallback(
    async (input: CreateUserInput): Promise<User> => {
      try {
        const result = await createUserMutation({
          variables: {
            userInput: {
              id: crypto.randomUUID(), // Generate unique ID
              chocoUser: true,
              name: input.name,
              email: input.email,
              phone: input.phone,
              locale: input.locale,
              isTest: input.isTest,
              supplier: input.supplier,
              loginMethod: mapLoginMethod(input.loginMethod),
              isPuppy: false,
              createdContext: 'userCreatedFromAdmin',
            },
          },
        });

        if (!result.data?.userCreate) {
          throw new Error('Failed to create user');
        }

        const createdUser = result.data.userCreate;

        // Map to generic User type using shared mapper
        const user = mapToUser(createdUser);

        addNotification({
          title: 'Success',
          message: 'User created successfully',
          type: 'success',
        });

        return user;
      } catch (error) {
        let errorMessage = 'Failed to create user. Please try again.';

        if (error instanceof ApolloError && error.graphQLErrors?.length > 0) {
          errorMessage = error.graphQLErrors[0].message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        addNotification({
          type: 'danger',
          title: 'User Creation Failed',
          message: errorMessage,
        });

        throw error;
      }
    },
    [createUserMutation, addNotification]
  );

  return {
    createUser,
    isLoading: loading,
  };
};
